/*
 * Copyright (c) 2022. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import _has from "lodash/has";
import _merge from "lodash/merge";
import * as applicationStatuses from "./applicationStatus";
import formSteps from "./formSteps";
import { getProductData } from "@/src/services/productService";
import {
  fromBoolean,
  fromDate,
  fromFloat,
  fromInsurer,
  fromNumber,
  getConvertedFormDataBase,
  handleSimpleArray,
  pickAndConvert,
  prepareFormDataBase,
  reduceSimpleArray,
  sanitizeAddressData,
  sanitizePersonData,
  toBoolean,
  toDate,
  toFloat,
  toInsurer,
  toNumber,
  trimValueIfSet,
} from "@/src/services/formServiceFunctions";
import ApiClient from "@/src/services/apiClient";
import { FormServiceBase, FormServiceSaveProps } from "@/src/types/FormServiceBase";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import { Customer } from "@/src/types/Entities/Customer";
import _cloneDeep from "lodash/cloneDeep";

type ApplicationStatus = typeof applicationStatuses[keyof typeof applicationStatuses];

class FormService extends FormServiceBase<ApplicationStatus> {
  dataFields = (): { [key in ApplicationStatus]?: { [key: string]: string[] } } => ({
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_BASE]: {
      dataLeakContract: ["contractDate"],
      customer: ["telephone"],
      "customer.person": ["gender", "givenName", "familyName"],
      "customer.person.residenceAddress": ["streetAddress", "postalCode", "addressLocality"],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_DETAILS]: {
      dataLeakContract: ["contractNumber", "salesChannel", "mobileProviderOrganizationId"],
      "customer.person": ["birthDate"],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTRACT_INSURANCE]: {
      caseInsurance: ["insurer", "insurancePolicyNumber", "caseReferenceNumber", "policyHolderType", "deductible"],
      "caseInsurance.policyHolder": ["gender", "givenName", "familyName"],
    },
    [applicationStatuses.APPLICATION_STATUS_PERSONAL_AFFECTION]: {
      "dataLeakContract.personalAffection": [
        "hadProblemsDueToCreditRating",
        "creditRatingProblemsDescription",
        "purchasesPlannedInTheNearFuture",
        "purchasesPlannedInTheNearFutureOther",
        "worriesFrequency",
        "worriesFrequencyOther",
        "emotionalState",
        "personalStatement",
      ],
    },
    [applicationStatuses.APPLICATION_STATUS_PERSONAL_AFFECTION_SUMMARY]: {
      "dataLeakContract.personalAffection": ["personalAffectionClient"],
    },
    [applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]: {
      dataLeakContract: ["powerOfAttorney"],
    },
  });

  conversionFields = [
    {
      path: "contractDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "customer.person.birthDate",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "legalExpenseInsurance",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "caseInsurance.deductible",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "mobileProviderOrganizationId",
      convertFrom: fromNumber,
      convertTo: toNumber,
    },
    {
      path: "caseInsurance.insurer",
      convertFrom: fromInsurer,
      convertTo: toInsurer,
    },
    {
      path: "personalAffection.hadProblemsDueToCreditRating",
      convertFrom: fromBoolean,
      convertTo: toBoolean,
    },
    {
      path: "personalAffection.emotionalState",
      convertFrom: reduceSimpleArray,
      convertTo: handleSimpleArray,
    },
    {
      path: "personalAffection.purchasesPlannedInTheNearFuture",
      convertFrom: reduceSimpleArray,
      convertTo: handleSimpleArray,
    },
  ];

  save = async ({
    formData,
    applicationStatus,
    nextApplicationStatus,
    product,
    customer,
    updateMutation,
    refreshCustomer,
  }: FormServiceSaveProps<ApplicationStatus>) => {
    const fields = this.dataFields()[applicationStatus] || {};
    let hasCustomerUpdates = false;

    if (_has(fields, "dataLeakContract.personalAffection")) {
      const convertedFormData = this.getConvertedFormData(formData);
      let personalAffectionData = pickAndConvert(convertedFormData.personalAffection, fields["dataLeakContract.personalAffection"]);
      personalAffectionData = this.sanitizePersonalAffectionData(personalAffectionData);

      await updateMutation.mutateAsync({
        uri: `/data_leak_personal_affections`,
        id: product.personalAffection.id,
        data: personalAffectionData,
      });
    }

    if (_has(fields, "customer.person.residenceAddress") && !!customer.person?.residenceAddress?.id) {
      hasCustomerUpdates = true;
      let addressData = pickAndConvert(formData.customer.person.residenceAddress, fields["customer.person.residenceAddress"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: customer.person.residenceAddress.id,
        data: addressData,
      });
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.client.residenceAddress.id,
        data: addressData,
      });
    }

    if (_has(fields, "customer.person") && !!customer.person?.id) {
      hasCustomerUpdates = true;
      const convertedFormData = this.getConvertedFormData(formData);
      let customerPersonData = pickAndConvert(convertedFormData.customer.person, fields["customer.person"]);
      customerPersonData = sanitizePersonData(customerPersonData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: customer.person.id,
        data: customerPersonData,
      });
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.client.id,
        data: customerPersonData,
      });
    }

    if (_has(fields, "customer")) {
      hasCustomerUpdates = true;
      const customerData = pickAndConvert(formData.customer, fields.customer);
      await updateMutation.mutateAsync({
        uri: "/customers",
        id: customer.id,
        data: customerData,
      });
    }

    if (_has(fields, "caseInsurance")) {
      const convertedFormData = this.getConvertedFormData(formData);
      let caseInsuranceData = pickAndConvert(convertedFormData.caseInsurance, fields["caseInsurance"]);
      await ApiClient.patch("/case_insurances/" + product.caseInsurance.id, { body: JSON.stringify(caseInsuranceData) });
    }

    if (_has(fields, "caseInsurance.policyHolder")) {
      const basePolicyHolderData = formData.caseInsurance.policyHolderType === "me" ? formData.customer.person : formData.caseInsurance.policyHolder;
      let policyHolderData = pickAndConvert(basePolicyHolderData, fields["caseInsurance.policyHolder"]);
      policyHolderData = sanitizePersonData(policyHolderData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.caseInsurance.policyHolder.id,
        data: policyHolderData,
      });
    }

    let productData: Partial<AbstractCase> = {};
    if (nextApplicationStatus) {
      productData.applicationStatus = nextApplicationStatus;
      this.statusFunctions[applicationStatus]?.();
    }

    if (_has(fields, product.name)) {
      productData = _merge(productData, pickAndConvert(formData, fields[product.name]));
      productData = this.getConvertedFormData(productData);
    }
    if (hasCustomerUpdates) {
      await refreshCustomer();
    }
    await updateMutation.mutateAsync({
      uri: getProductData(product.name, "apiUri"),
      id: product.id,
      data: productData,
    });
  };

  sanitizePersonalAffectionData = (personalAffectionData: Record<string, any>) => {
    let sanitizedPersonalAffectionData = _cloneDeep(personalAffectionData);
    sanitizedPersonalAffectionData = trimValueIfSet("personalStatement", sanitizedPersonalAffectionData);
    sanitizedPersonalAffectionData = trimValueIfSet("purchasesPlannedInTheNearFutureOther", sanitizedPersonalAffectionData);
    sanitizedPersonalAffectionData = trimValueIfSet("creditRatingProblemsDescription", sanitizedPersonalAffectionData);

    if (sanitizedPersonalAffectionData.hadProblemsDueToCreditRating === false) {
      sanitizedPersonalAffectionData.creditRatingProblemsDescription = null;
    }

    return sanitizedPersonalAffectionData;
  };

  getConvertedFormData = (data: FormServiceSaveProps<ApplicationStatus>["formData"]) => {
    return getConvertedFormDataBase(data, this.conversionFields);
  };

  prepareFormData = (product: AbstractCase, customer: Customer): AbstractCase => {
    return prepareFormDataBase(product, customer, this.conversionFields);
  };

  getAvailableSteps = (formData: Record<string, any>, product: AbstractCase) => {
    let availableSteps = [];
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_BASE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_DETAILS]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_CONTRACT_INSURANCE]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_PERSONAL_AFFECTION]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_PERSONAL_AFFECTION_SUMMARY]);
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_SUMMARY]);
    //for dependencies that are changed in the same step as we are we need to check product instead of formdata to not
    // invalidate our next step since formdata updates before the actual request is fired
    if (!product.powerOfAttorney) {
      availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_POWER_OF_ATTORNEY]);
    }
    availableSteps.push(formSteps[applicationStatuses.APPLICATION_STATUS_DONE]);
    return availableSteps;
  };
}

export default new FormService();
